/**
 * Utilitaires de cache avec TTL (Time To Live)
 * Pour optimiser les chargements et réduire les appels API
 */

const CACHE_PREFIX = 'heedify_context_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes par défaut

/**
 * Sauvegarde des données dans le cache avec TTL
 * @param {string} key - Clé du cache
 * @param {any} data - Données à cacher
 * @param {number} ttl - Durée de vie en millisecondes
 */
export const setCache = (key, data, ttl = DEFAULT_TTL) => {
  try {
    const item = {
      data,
      timestamp: Date.now(),
      ttl
    };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(item));
    console.log(`✅ Cache saved: ${key} (TTL: ${ttl}ms)`);
  } catch (error) {
    console.warn('⚠️ Cache save failed:', error);
  }
};

/**
 * Récupère des données du cache (si pas expirées)
 * @param {string} key - Clé du cache
 * @returns {any|null} - Données ou null si expiré/inexistant
 */
export const getCache = (key) => {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + key);
    if (!cached) return null;

    const item = JSON.parse(cached);
    const now = Date.now();
    
    // Vérifier si expiré
    if (now - item.timestamp > item.ttl) {
      console.log(`⏰ Cache expired: ${key}`);
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    console.log(`✅ Cache hit: ${key}`);
    return item.data;
  } catch (error) {
    console.warn('⚠️ Cache read failed:', error);
    return null;
  }
};

/**
 * Invalide une clé de cache
 * @param {string} key - Clé à invalider
 */
export const invalidateCache = (key) => {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
    console.log(`🗑️ Cache invalidated: ${key}`);
  } catch (error) {
    console.warn('⚠️ Cache invalidation failed:', error);
  }
};

/**
 * Nettoie tout le cache de l'app
 */
export const clearAllCache = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('🗑️ All cache cleared');
  } catch (error) {
    console.warn('⚠️ Cache clear failed:', error);
  }
};

/**
 * Récupère du cache ou exécute la fonction
 * Pattern stale-while-revalidate
 * @param {string} key - Clé du cache
 * @param {Function} fetchFn - Fonction pour récupérer les données
 * @param {number} ttl - Durée de vie
 * @returns {Promise<any>}
 */
export const getCacheOrFetch = async (key, fetchFn, ttl = DEFAULT_TTL) => {
  const cached = getCache(key);
  
  if (cached) {
    // Retourner le cache immédiatement
    // Et rafraîchir en arrière-plan si proche de l'expiration
    const item = JSON.parse(localStorage.getItem(CACHE_PREFIX + key));
    const age = Date.now() - item.timestamp;
    
    if (age > ttl * 0.8) { // 80% du TTL
      console.log(`🔄 Background refresh: ${key}`);
      fetchFn().then(data => setCache(key, data, ttl)).catch(console.error);
    }
    
    return cached;
  }
  
  // Pas de cache, fetch et sauvegarder
  const data = await fetchFn();
  setCache(key, data, ttl);
  return data;
};

// Clés de cache prédéfinies
export const CACHE_KEYS = {
  AGENT_PROFILE: 'agent_profile',
  CRM_DATA: 'crm_data',
  MEETING_DETAILS: 'meeting_details'
};
